import React, { useMemo, useState, useEffect } from "react";
import classNames from "classnames";

import classes from "./styles.module.scss";

const formatCount = (value) => Number(value || 0).toLocaleString();

/** Already completed runs */
const USED_COLOR = "#18C1AD";
/** Queued / not-yet-run combinations */
const PENDING_COLOR = "#FFB22D";
/** Demand exceeds unlocked capacity */
const OVER_COLOR = "#F58020";

const UsageBar = ({
    usage = 0,
    toBeUsed = 0,
    total = 0,
    mainColor = USED_COLOR,
    secondaryColor = PENDING_COLOR,
    showDetails = false,
    className,
}) => {
    const [isAnimated, setIsAnimated] = useState(false);

    const { usedPercent, pendingPercent, overflowPercent, label, isOverCapacity } = useMemo(() => {
        const usedCount = Number(usage) || 0;
        const pendingCount = Number(toBeUsed) || 0;
        const capacity = Number(total) || 0;
        const demand = usedCount + pendingCount;

        if (capacity <= 0) {
            return {
                usedPercent: 0,
                pendingPercent: 0,
                overflowPercent: demand > 0 ? 100 : 0,
                label: demand > 0 ? "Over" : "0%",
                isOverCapacity: demand > 0,
            };
        }

        const over = demand > capacity;
        const usedPct = Math.min(100, Math.max(0, (usedCount / capacity) * 100));
        const remainingCapacity = Math.max(0, capacity - usedCount);
        const pendingWithin = Math.min(pendingCount, remainingCapacity);
        const pendingPct = Math.min(100 - usedPct, Math.max(0, (pendingWithin / capacity) * 100));
        // Visual overflow tip when over: fill any leftover track with orange
        const overflowPct = over ? Math.max(0, 100 - usedPct - pendingPct) : 0;
        const committedPct = Math.round((demand / capacity) * 100);

        return {
            usedPercent: usedPct,
            pendingPercent: pendingPct,
            overflowPercent: overflowPct,
            label: over ? "Over" : `${Math.min(committedPct, 100)}%`,
            isOverCapacity: over,
        };
    }, [usage, toBeUsed, total]);

    useEffect(() => {
        const frame = requestAnimationFrame(() => setIsAnimated(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    return (
        <div
            className={classNames(
                classes.usageBar,
                isAnimated && classes.usageBarReady,
                showDetails && classes.usageBarWithDetails,
                isOverCapacity && classes.usageBarExceeded,
                className
            )}
        >
            {showDetails && (
                <div className={classes.usageBarDetails}>
                    <span>
                        <strong>{formatCount(usage)}</strong> used
                    </span>
                    <span className={classes.usageBarDetailDivider} />
                    <span>
                        <strong>{formatCount(toBeUsed)}</strong> pending
                    </span>
                    <span className={classes.usageBarDetailDivider} />
                    <span className={isOverCapacity ? classes.usageBarOver : undefined}>
                        <strong>{formatCount(total)}</strong> unlocked
                    </span>
                    {isOverCapacity && (
                        <>
                            <span className={classes.usageBarDetailDivider} />
                            <span className={classes.usageBarOverBadge}>Over capacity</span>
                        </>
                    )}
                </div>
            )}
            <div className={classes.usageBarRow}>
                <div
                    className={classNames(classes.usageBarTrack, isOverCapacity && classes.usageBarTrackExceeded)}
                    style={{ backgroundColor: "#E1EAF0" }}
                >
                    <div
                        className={classes.usageBarFill}
                        style={{
                            width: isAnimated ? `${usedPercent}%` : "0%",
                            backgroundColor: mainColor,
                        }}
                    />
                    <div
                        className={classNames(
                            classes.usageBarPending,
                            pendingPercent > 0 && classes.usageBarPendingActive
                        )}
                        style={{
                            width: isAnimated ? `${pendingPercent}%` : "0%",
                            backgroundColor: secondaryColor,
                        }}
                    />
                    {overflowPercent > 0 && (
                        <div
                            className={classNames(classes.usageBarOverflow, classes.usageBarPendingExceeded)}
                            style={{
                                width: isAnimated ? `${overflowPercent}%` : "0%",
                                backgroundColor: OVER_COLOR,
                            }}
                        />
                    )}
                </div>
                <span
                    key={`${label}-${total}`}
                    className={classNames(classes.usageBarLabel, isOverCapacity && classes.usageBarLabelExceeded)}
                >
                    {label}
                </span>
            </div>
        </div>
    );
};

export default UsageBar;
